import { PaginationProps, Spin, TableProps } from 'antd';
import { deleteApi, getApi } from 'apis';
import PopUpWrapper from 'components/Global/PopUpWrapper';
import TableActionHeader from 'components/Global/TableActionHeader';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import dayjs from 'dayjs';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import CustomTable from './CustomTable';
import DraftMedicineEditBody from './DraftMedicineEditBody';
import MapMedicineModal from './MapMedicineModal';
import { fetchDraftMedicineEditBundle } from './fetchDraftEditBundle';
import { useDebounce } from 'utils/hooks';
import { toastText } from 'utils/utils';
import type { DraftEditPrefetchedReferenceData, DraftMedicineRequestRow, MedicineDetailsByIdsRow } from './types';
import type { StatusFilterOption, SearchFieldOption } from './SearchAndFilter/types';

const DRAFT_TABLE_SIZE_KEY = 'draftMedicineTableSize';
const DRAFT_LIST_PATH = '/draft-medicine-request/list';

const DraftMedicinesTable = () => {
	const [search, setSearch] = useState('');
	const [searchField, setSearchField] = useState<SearchFieldOption>('medicineName');
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(
		Number(localStorage.getItem(DRAFT_TABLE_SIZE_KEY)) || 10
	);
	const [sorterField, setSorterField] = useState('createdAt');
	const [sorterOrder, setSorterOrder] = useState<'asc' | 'desc'>('asc');
	const [medicinesData, setMedicinesData] = useState<DraftMedicineRequestRow[]>([]);
	const [totalMedicines, setTotalMedicines] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedDate, setSelectedDate] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
	const [statusFilter, setStatusFilter] = useState<StatusFilterOption | null>('PENDING');
	const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
	const [categories, setCategories] = useState<{ id: string; categoryName: string }[]>([]);

	const [isMapModalOpen, setIsMapModalOpen] = useState(false);
	const [mapTargetRow, setMapTargetRow] = useState<DraftMedicineRequestRow | null>(null);

	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [rowToDelete, setRowToDelete] = useState<DraftMedicineRequestRow | null>(null);

	const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
	const [editDrawerTitle, setEditDrawerTitle] = useState('Edit draft request');
	const [editHydrationStatus, setEditHydrationStatus] = useState<'idle' | 'loading' | 'ready'>('idle');
	const [editPrefetchedData, setEditPrefetchedData] = useState<DraftEditPrefetchedReferenceData | null>(null);
	const [editSelectedMedicine, setEditSelectedMedicine] = useState<
		(MedicineDetailsByIdsRow & { draftRequestId?: string }) | undefined
	>();
	const editLoadGenRef = useRef(0);
	const [optionsState, setOptionsState] = useState({
		medicineName: [] as { label: string; value: string }[],
		manufacturer: [] as { label: string; value: string }[],
		marketedBy: [] as { label: string; value: string }[],
		weightage: [] as { label: string; value: string }[],
		numberOfItems: [] as { label: string; value: string }[],
		flavors: [] as { label: string; value: string }[],
		offers: [] as { label: string; value: string }[],
		saltComposition: [] as { label: string; value: string }[],
		usesTags: [] as { label: string; value: string }[],
	});

	const debouncedSearch = useDebounce(search, 500);

	const isProfileLoading = useSelector(
		(state: any) => state?.userProfile?.isLoading,
	);
	const profile = useSelector((state: any) => state?.userProfile?.data);
	const isAdmin = profile?.isAdmin === true;

	useEffect(() => {
		if (isProfileLoading) return;
		if (!isAdmin && statusFilter === 'DELETED') {
			setStatusFilter('PENDING');
		}
		if (!isAdmin && searchField === 'pharmacyName') {
			setSearchField('medicineName');
		}
	}, [isAdmin, isProfileLoading, statusFilter, searchField]);

	const fetchDraftRequests = useCallback(async () => {
		setIsLoading(true);
		const query: Record<string, unknown> = {
			page,
			limit,
			targetField: searchField,
			sorterField: sorterField || 'medicineName',
			type: sorterOrder,
		};
		const trimmed = debouncedSearch.trim();
		if (trimmed.length > 0) {
			query.search = trimmed;
		}
		if (selectedDate?.[0] && selectedDate?.[1]) {
			query.selectedDate = [
				selectedDate[0].toISOString(),
				selectedDate[1].toISOString(),
			];
		}
		if (statusFilter) {
			query.status = statusFilter;
		}
		if (isAdmin && categoryFilter) {
			query.categoryId = categoryFilter;
		}
		try {
			const res = await getApi(DRAFT_LIST_PATH, query);
			setMedicinesData(res?.data?.data || []);
			setTotalMedicines(typeof res?.data?.total === 'number' ? res.data.total : 0);
		} catch (error: any) {
			toastText(
				error?.response?.data?.error?.errorDescription ??
					error?.response?.data?.message ??
					'Failed to load draft medicine requests',
				'error'
			);
			setMedicinesData([]);
			setTotalMedicines(0);
		} finally {
			setIsLoading(false);
		}
	}, [
		page,
		limit,
		debouncedSearch,
		searchField,
		sorterField,
		sorterOrder,
		selectedDate,
		statusFilter,
		categoryFilter,
		isAdmin,
	]);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, searchField, statusFilter, categoryFilter, selectedDate]);

	useEffect(() => {
		if (isProfileLoading) return;
		fetchDraftRequests();
	}, [fetchDraftRequests, isProfileLoading]);

	useEffect(() => {
		if (!isAdmin) return;
		(async () => {
			try {
				const res = await getApi('/category/get-all', { limit: 1000 });
				if (res?.data?.data) {
					setCategories(
						res.data.data.map((c: { id: string; categoryName: string }) => ({
							id: c.id,
							categoryName: c.categoryName,
						}))
					);
				}
			} catch {
				// ignore
			}
		})();
	}, [isAdmin]);

	const editHandler = (row: DraftMedicineRequestRow) => {
		const gen = ++editLoadGenRef.current;
		setIsEditDrawerOpen(true);
		setEditDrawerTitle('Edit draft request');
		setEditHydrationStatus('loading');
		setEditPrefetchedData(null);
		setEditSelectedMedicine(undefined);

		(async () => {
			try {
				// Load by MedicineItem.id (FK on DraftMedicineRequest), not draft request row.id
				const bundle = await fetchDraftMedicineEditBundle(row.medicineId);
				if (gen !== editLoadGenRef.current) return;

				const hydrated: MedicineDetailsByIdsRow & { draftRequestId: string } = {
					...bundle.medicine,
					draftRequestId: row.id,
					medicineSecondaryImageUrl: bundle.medicine.medicineSecondaryImageUrl ?? [],
					medicinePrimaryImageUrl: bundle.medicine.medicinePrimaryImageUrl ?? '',
					saltComposition: bundle.medicine.saltComposition ?? [],
					usesTags: Array.isArray(bundle.medicine.usesTags) ? bundle.medicine.usesTags : [],
				};

				setEditPrefetchedData({
					wholeCategories: bundle.wholeCategories,
					categoryList: bundle.categoryList,
					subCategoryList: bundle.subCategoryList,
					doseForms: bundle.doseForms,
					doseFormOptionsForCategory: bundle.doseFormOptionsForCategory,
					warningTags: bundle.warningTags,
				});
				setEditSelectedMedicine(hydrated);
				setEditHydrationStatus('ready');
			} catch (error: any) {
				if (gen !== editLoadGenRef.current) return;
				toastText(
					error?.response?.data?.error?.errorDescription ??
						error?.response?.data?.message ??
						error?.message ??
						'Failed to load draft for editing',
					'error'
				);
				setIsEditDrawerOpen(false);
				setEditHydrationStatus('idle');
				setEditPrefetchedData(null);
				setEditSelectedMedicine(undefined);
			}
		})();
	};

	const closeEditDrawer = () => {
		editLoadGenRef.current += 1;
		setIsEditDrawerOpen(false);
		setEditHydrationStatus('idle');
		setEditPrefetchedData(null);
		setOptionsState({
			medicineName: [],
			manufacturer: [],
			marketedBy: [],
			weightage: [],
			numberOfItems: [],
			flavors: [],
			offers: [],
			saltComposition: [],
			usesTags: [],
		});
		setEditSelectedMedicine(undefined);
	};

	const mapHandler = (row: DraftMedicineRequestRow) => {
		setMapTargetRow(row);
		setIsMapModalOpen(true);
	};

	const closeMapModal = () => {
		setIsMapModalOpen(false);
		setMapTargetRow(null);
	};

	const openDeleteModal = (row: DraftMedicineRequestRow) => {
		setRowToDelete(row);
		setIsDeleteModalOpen(true);
	};

	const closeDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setRowToDelete(null);
	};

	const deleteHandler = async () => {
		if (!rowToDelete?.id) return;
		try {
			setIsDeleteLoading(true);
			const resp = await deleteApi(`/draft-medicine-request/delete/${rowToDelete.id}`);
			toastText(
				resp?.data?.message ?? 'Draft medicine request deleted successfully',
				'success'
			);
		} catch (error: any) {
			toastText(
				error?.response?.data?.error?.errorDescription ??
					error?.response?.data?.message ??
					'Something went wrong while deleting draft medicine request',
				'error'
			);
		} finally {
			setIsDeleteLoading(false);
			closeDeleteModal();
			fetchDraftRequests();
		}
	};

	const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
		setSearch(event.target.value);
	};

	const handleTableChange: TableProps<DraftMedicineRequestRow>['onChange'] = (
		_pagination,
		_filters,
		sorter
	) => {
		const order = Array.isArray(sorter) ? sorter[0]?.order : sorter.order;
		const field = Array.isArray(sorter) ? sorter[0]?.field : sorter.field;
		setSorterOrder(order === 'descend' ? 'desc' : 'asc');
		const f = typeof field === 'string' ? field : String(field ?? 'medicineName');
		setSorterField(f || 'medicineName');
	};

	const onShowSizeChange: PaginationProps['onShowSizeChange'] = (current, pageSize) => {
		setPage(current);
		setLimit(pageSize);
		localStorage.setItem(DRAFT_TABLE_SIZE_KEY, String(pageSize));
	};

	return (
		<>
			<div>
				<TableActionHeader title="Draft Medicines Request" />

				<CustomTable
					dataSource={medicinesData}
					handleTableChange={handleTableChange}
					onShowSizeChange={onShowSizeChange}
					isLoading={isLoading}
					search={search}
					totalRecords={totalMedicines}
					performSearchHandler={performSearchHandler}
					setSearch={setSearch}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					onEditDraftRequest={editHandler}
					onMapDraftRequest={mapHandler}
					onDeleteDraftRequest={openDeleteModal}
					currentPage={page}
					pageSize={limit}
					statusFilter={statusFilter}
					onStatusFilterChange={setStatusFilter}
					categoryFilter={categoryFilter}
					onCategoryFilterChange={setCategoryFilter}
					categories={categories}
					searchField={searchField}
					onSearchFieldChange={setSearchField}
				/>
			</div>

			{isEditDrawerOpen && (
				<PopUpWrapper
					title={editDrawerTitle}
					isPoppedUp={isEditDrawerOpen}
					closeDrawerByAnimation={closeEditDrawer}
				>
					{editHydrationStatus === 'loading' && (
						<div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
							<Spin size="large" />
						</div>
					)}
					{editHydrationStatus === 'ready' && editSelectedMedicine && editPrefetchedData && (
						<DraftMedicineEditBody
							key={`${editSelectedMedicine.id}-${editSelectedMedicine.draftRequestId ?? ''}`}
							closeDrawerByAnimation={closeEditDrawer}
							editSelectedMedicine={editSelectedMedicine}
							setEditSelectedMedicine={setEditSelectedMedicine}
							fetchMedicines={fetchDraftRequests}
							setOptionsState={setOptionsState}
							optionsState={optionsState}
							prefetchedReferenceData={editPrefetchedData}
						/>
					)}
				</PopUpWrapper>
			)}

			<MapMedicineModal
				open={isMapModalOpen}
				draftRequest={mapTargetRow}
				onClose={closeMapModal}
				onMapped={fetchDraftRequests}
			/>

			<ConfirmDelete
				handleCancel={closeDeleteModal}
				handleOk={closeDeleteModal}
				isModalOpen={isDeleteModalOpen}
				deleteHandler={deleteHandler}
				isLoading={isDeleteLoading}
				selectedDeleteData={rowToDelete?.medicineName}
				deleteHeading="Draft Medicine Request"
			/>
		</>
	);
};

export default DraftMedicinesTable;
